/**
 * lib/attendanceCalc.ts
 * ------------------------------------------------------------------
 * Computes attendance statistics from daily attendance records.
 *
 * Primary source: `reportDailyAttendance` (per-day records).
 * Fallback: `reportAttendance` (legacy term totals) for students
 * without daily records yet.
 *
 * Used by: Student Profile quick stats, report generation,
 * daily attendance summary view.
 * ------------------------------------------------------------------
 */
import type { AttendanceStatus, DailyAttendanceDoc } from '@/types';

/* ==================================================================
   TYPES
   ================================================================== */

export interface StudentAttendanceSummary {
  studentId: string;
  totalDays: number;
  present: number;
  absent: number;
  late: number;
  excused: number;
  sick: number;
  attendancePercent: number | null; // null if no records
}

export interface ClassAttendanceSummary {
  classId: string;
  totalDays: number; // number of days attendance was taken
  students: StudentAttendanceSummary[];
  averageAttendancePercent: number | null;
}

export interface DaySnapshot {
  date: string;
  present: number;
  absent: number;
  late: number;
  excused: number;
  sick: number;
  total: number;
}

/* ==================================================================
   STATUS HELPERS
   ================================================================== */

/** Statuses that count as "attended" for percentage calculation. */
export function isPresent(status: AttendanceStatus): boolean {
  return status === 'present' || status === 'late';
}

/**
 * Statuses that count as "absent". Per school policy (Aug 2026) SICK is
 * regarded as an absence — the pupil was not in class — so it counts here
 * alongside 'absent'. 'excused' remains a separate, authorised category and
 * 'late' still counts as attended.
 */
export function isAbsentStatus(status: AttendanceStatus): boolean {
  return status === 'absent' || status === 'sick';
}

export const STATUS_LABELS: Record<AttendanceStatus, string> = {
  present: 'Present',
  absent: 'Absent',
  late: 'Late',
  excused: 'Excused',
  sick: 'Sick',
};

export const STATUS_COLORS: Record<AttendanceStatus, string> = {
  present: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700',
  absent: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700',
  late: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700',
  excused: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700',
  sick: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700',
};

export const STATUS_SHORT: Record<AttendanceStatus, string> = {
  present: 'P',
  absent: 'A',
  late: 'L',
  excused: 'E',
  sick: 'S',
};

/* ==================================================================
   COMPUTATION
   ================================================================== */

/**
 * Compute per-student attendance summary from daily attendance docs.
 * Pass only the docs for a specific class within a date range (term).
 */
export function computeClassAttendance(
  classId: string,
  docs: DailyAttendanceDoc[],
  studentIds: string[],
): ClassAttendanceSummary {
  const totalDays = docs.length;

  const students: StudentAttendanceSummary[] = studentIds.map((sid) => {
    let present = 0;
    let absent = 0;
    let late = 0;
    let excused = 0;
    let sick = 0;

    for (const d of docs) {
      const status = d.entries[sid];
      if (!status) continue; // student not recorded this day
      switch (status) {
        case 'present': present++; break;
        case 'absent': absent++; break;
        case 'late': late++; break;
        case 'excused': excused++; break;
        case 'sick': sick++; break;
      }
    }

    const recordedDays = present + absent + late + excused + sick;
    // Attendance % = (present + late) / total recorded days
    // Late counts as attended (they showed up, just not on time)
    // Excused and sick are separate from absent
    const attendancePercent = recordedDays > 0
      ? Math.round(((present + late) / recordedDays) * 100)
      : null;

    return { studentId: sid, totalDays: recordedDays, present, absent, late, excused, sick, attendancePercent };
  });

  const pcts = students.map((s) => s.attendancePercent).filter((p): p is number => p !== null);
  const averageAttendancePercent = pcts.length > 0
    ? Math.round(pcts.reduce((a, b) => a + b, 0) / pcts.length)
    : null;

  return { classId, totalDays, students, averageAttendancePercent };
}

/**
 * Compute a per-day snapshot for charting / overview.
 */
export function computeDaySnapshots(docs: DailyAttendanceDoc[]): DaySnapshot[] {
  return docs
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((d) => {
      let present = 0, absent = 0, late = 0, excused = 0, sick = 0;
      for (const status of Object.values(d.entries)) {
        switch (status) {
          case 'present': present++; break;
          case 'absent': absent++; break;
          case 'late': late++; break;
          case 'excused': excused++; break;
          case 'sick': sick++; break;
        }
      }
      return { date: d.date, present, absent, late, excused, sick, total: present + absent + late + excused + sick };
    });
}

/**
 * Convert daily attendance into the legacy term-total format
 * (daysAbsent per student) for backward compatibility with
 * existing report generation and ReviewWorkspace.
 */
export function toLegacyTotals(
  docs: DailyAttendanceDoc[],
  studentIds: string[],
): Record<string, { daysAbsent: number }> {
  const result: Record<string, { daysAbsent: number }> = {};
  for (const sid of studentIds) {
    let absent = 0;
    for (const d of docs) {
      const status = d.entries[sid];
      // SICK counts as an absence for the report's "days absent" (policy Aug 2026).
      if (status === 'absent' || status === 'sick') absent++;
    }
    result[sid] = { daysAbsent: absent };
  }
  return result;
}
